import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { tariffesArraySelector, userSelector } from "../../store/selectors";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import plusIcon from "../../assets/plus-icon.svg";
import "./AdminStatsScreen.css";
import momentTz from "moment-timezone";
import moment from "moment";
import ProfileSidebar from "../../components/ProfileSidebar/ProfileSidebar";
import Avatar from "../../components/Avatar/Avatar";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import AppLogo from "../../assets/AppLogoBlue.svg";
import { ReactComponent as MenuIcon } from "../../assets/mobile/menu.svg";
import NotificationsPopup from "../../components/NotificationsPopup/NotificationsPopup";
import {
  deleteTariff,
  fetchLatestStats,
  fetchMetricData,
  fetchOrganizationsTariffes,
  fetchTariffes,
} from "../../store/tariffesSlice";
import AddTariffModal from "../../components/AddTariffModal/AddTariffModal";
import EditTariffModal from "../../components/EditTariffModal/EditTariffModal";
import TariffVersionsModal from "../../components/TariffVersionsModal/TariffVersionsModal";
import OrganizationChangeTariffModal from "../../components/OrganizationChangeTariffModal/OrganizationChangeTariffModal";
import ProfilePopup from "../../components/ProfilePopup/ProfilePopup";
import { Chart } from "react-charts";
import useIsMobile from "../../hooks/useIsMobile";

const eternalTariffesIds = [8, 19, 21];
const DATE_FILTERS = [
  { value: "DAY", label: "Сутки" },
  { value: "WEEK", label: "Неделя" },
  { value: "MONTH", label: "Месяц" },
  { value: "ALL", label: "Всё время" },
];

const AdminStatsScreen = () => {
  const user = useSelector(userSelector);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t } = useTranslation();
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState("TOTAL_USERS");
  const isMobile = useIsMobile();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [dateFilter, setDateFilter] = useState("MONTH");
  const [tariffesString, setTariffesString] = useState("");
  const [metricsData, setMetricsData] = useState({
    TOTAL_USERS: [],
    TOTAL_UPLOADED_VIDEOS: [],
    TOTAL_STORAGE_USED_MB: [],
    TOTAL_VIDEO_DURATION_SEC: [],
    TOTAL_COMMENTS: [],
    TOTAL_ACTIVE_SUBSCRIPTIONS: [],
  });
  const userName = user?.email;
  const [statsArray, setStatsArray] = useState([]);

  const metricLabel = {
    TOTAL_USERS: "Всего пользователей",
    TOTAL_UPLOADED_VIDEOS: "Всего загружено видео",
    TOTAL_STORAGE_USED_MB: "Текущий объем используемого хранилища",
    TOTAL_VIDEO_DURATION_SEC: "Общая длительность видео",
    TOTAL_COMMENTS: "Всего комментариев",
    TOTAL_ACTIVE_SUBSCRIPTIONS: "Всего активных подписок",
  };

  const metricLabelDiff = {
    TOTAL_USERS: "Новых пользователей за день",
    TOTAL_UPLOADED_VIDEOS: "Загружено видео за день",
    TOTAL_STORAGE_USED_MB: "Изменение объема используемого хранилища за день",
    TOTAL_VIDEO_DURATION_SEC: "Длительность видео (изменение за день)",
    TOTAL_COMMENTS: "Комментариев за день",
    TOTAL_ACTIVE_SUBSCRIPTIONS: "Новых подписок",
  };

  useEffect(() => {
    document.title = "7cut • Админка";
  }, []);

  useEffect(() => {
    updateInfo();
  }, []);

  const onChangeDateFilter = (value) => {
    setDateFilter(value);
    updateInfo(value);
  };

  const updateInfo = async (newDateFilter = dateFilter) => {
    const toDate = moment().format("YYYY-MM-DD");
    let fromDate = "2025-07-10";
    if (newDateFilter === "DAY") {
      fromDate = moment().subtract(2, "days").format("YYYY-MM-DD");
    } else if (newDateFilter === "WEEK") {
      fromDate = moment().subtract(8, "days").format("YYYY-MM-DD");
    } else if (newDateFilter === "MONTH") {
      fromDate = moment()
        .subtract(1, "months")
        .subtract(1, "days")
        .format("YYYY-MM-DD");
    }

    const metricsCodes = [
      "TOTAL_USERS",
      "TOTAL_UPLOADED_VIDEOS",
      "TOTAL_STORAGE_USED_MB",
      "TOTAL_VIDEO_DURATION_SEC",
      "TOTAL_COMMENTS",
      "TOTAL_ACTIVE_SUBSCRIPTIONS",
    ];

    const newMetricsData = {
      TOTAL_USERS: 0,
      TOTAL_UPLOADED_VIDEOS: 0,
      TOTAL_STORAGE_USED_MB: 0,
      TOTAL_VIDEO_DURATION_SEC: 0,
      TOTAL_COMMENTS: 0,
      TOTAL_ACTIVE_SUBSCRIPTIONS: 0,
    };

    for (let i = 0; i < metricsCodes.length; i++) {
      const metricCode = metricsCodes[i];
      const res = await dispatch(
        fetchMetricData({
          fromDate,
          toDate,
          metricCode,
        })
      );
      if (!res.error) {
        const initialArray = res.payload;
        const originalArray = initialArray.slice(1);
        const onlyNoOrg = originalArray
          .filter((item) => !!item.value)
          .map((item) => ({
            // ...item,
            date: item.date.substr(8, 2) + "." + item.date.substr(5, 2),
            value:
              metricCode === "TOTAL_STORAGE_USED_MB"
                ? +item.value.toFixed(2)
                : metricCode === "TOTAL_VIDEO_DURATION_SEC"
                  ? +(item.value / 1000).toFixed(0)
                  : item.value,
          }));
        newMetricsData[metricCode] = onlyNoOrg;
        const diffArray = onlyNoOrg.map((data, index) => {
          const indexInInitialArray = index + 1;
          const difference =
            initialArray[indexInInitialArray].value -
            initialArray[indexInInitialArray - 1].value;
          return {
            date: data.date,
            value:
              metricCode === "TOTAL_STORAGE_USED_MB"
                ? +difference.toFixed(2)
                : metricCode === "TOTAL_VIDEO_DURATION_SEC"
                  ? +(difference / 1000).toFixed(0)
                  : metricCode === "TOTAL_COMMENTS" ||
                      metricCode === "TOTAL_ACTIVE_SUBSCRIPTIONS"
                    ? Math.max(difference, 0)
                    : difference,
          };
        });
        newMetricsData[`${metricCode}_diff`] = diffArray;
      }
    }
    setMetricsData(newMetricsData);
  };

  const formatDate = (dateString) => {
    const res = momentTz(dateString).tz("etc/gmt-3").format("DD.MM.YYYY");
    return res;
  };

  const updateTariffesData = () => {
    setTariffesString("");
    dispatch(fetchOrganizationsTariffes({})).then((res) => {
      if (!res.error) {
        let result = {};
        const tariffesArray = res.payload.content;

        for (let i = 0; i < tariffesArray.length; i++) {
          const tariffInfo = tariffesArray[i];
          const tariffName = tariffInfo.subscription?.plan?.name || "noop";
          if (!result[tariffName]) {
            result[tariffName] = {
              name: tariffName,
              value: 1,
            };
          } else {
            result[tariffName] = {
              name: tariffName,
              value: result[tariffName].value + 1,
            };
          }
        }

        const totalInfoString = Object.values(result).reduce(
          (acc, curr, index, initial) => {
            if (curr.name === "noop") return acc;
            if (index === Object.values(result).length - 1)
              return acc + `"${curr.name}": ${curr.value}.`;
            return acc + `"${curr.name}": ${curr.value}, `;
          },
          ""
        );
        setTariffesString(totalInfoString);
      }
    });
  };

  const getTotalMetricCount = (valuesArray) => {
    if (!valuesArray || valuesArray.length === 0) return 0;
    if (activeTab === "TOTAL_USERS") {
      return valuesArray[valuesArray.length - 1].value;
    }
    if (activeTab === "TOTAL_UPLOADED_VIDEOS") {
      return valuesArray[valuesArray.length - 1].value;
    }

    if (activeTab === "TOTAL_ACTIVE_SUBSCRIPTIONS") {
      return valuesArray[valuesArray.length - 1].value;
    }

    if (activeTab === "TOTAL_STORAGE_USED_MB") {
      const valueInMb = valuesArray[valuesArray.length - 1].value;
      let result = valueInMb;

      if (valueInMb > 1024) {
        result = (valueInMb / 1024).toFixed(3).toString() + " Гб";
      } else {
        result += " Мб";
      }

      return result;
    }

    if (activeTab === "TOTAL_VIDEO_DURATION_SEC") {
      const valueInSeconds = valuesArray[valuesArray.length - 1].value;
      let result = valueInSeconds;

      if (valueInSeconds > 3600) {
        let numberOfHours = Math.floor(+valueInSeconds / 3600);
        let secondsLeft = Math.floor(+valueInSeconds - 3600 * numberOfHours);
        let numberOfMinutes = Math.floor(+secondsLeft / 60);
        let numberOfSeconds = Math.floor(+secondsLeft - 60 * numberOfMinutes);

        result = `${numberOfHours} ч. ${numberOfMinutes} мин. ${numberOfSeconds} сек.`;
      } else if (valueInSeconds > 60) {
        let numberOfMinutes = Math.floor(+valueInSeconds / 60);
        let numberOfSeconds = Math.floor(
          +valueInSeconds - 60 * numberOfMinutes
        );
        result = `${numberOfMinutes} мин. ${numberOfSeconds} сек.`;
      } else {
        result += " сек.";
      }

      return result;
    }

    if (activeTab === "TOTAL_COMMENTS") {
      return valuesArray[valuesArray.length - 1].value;
    }
  };

  const yesterdayDate = formatDate(momentTz().toISOString());

  return (
    <div className={isMobile ? "App-mobile" : "adminScreen-App"}>
      {/* HEADER */}
      <div
        className={isMobile ? "mainScreenHeader-mobile" : "adminScreen-header"}
      >
        <div className={isMobile ? "header-left-mobile" : "header-left"}>
          {isMobile && (
            <div
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              style={{ display: "flex", marginTop: 7 }}
            >
              <MenuIcon color="white" />
            </div>
          )}
          <div
            onClick={() => navigate("/home", { replace: true })}
            className={"mainScreen-logoBlock"}
            style={{ cursor: "pointer" }}
          >
            <img src={AppLogo} style={{ height: "100%" }} alt="logo" />
          </div>
        </div>
        <div className="header-actions">
          <NotificationsPopup />
          <ProfilePopup />
        </div>
      </div>
      <div className="admin-container" style={{ alignItems: "flex-start" }}>
        {((!isMobile || isMenuOpen) && (
          <ProfileSidebar activeTab="admin" subActive="stats" />
        )) ||
          null}

        {/* <aside className="admin-sidebar">
          <nav>
            <ul>
              <li className="active">Дашборд</li>
            </ul>
            <ul onClick={() => navigate("/profile/admin/tariffes")}>
              <li className="">Тарифы</li>
            </ul>
            <ul onClick={() => navigate("/profile/admin/organizations")}>
              <li>Организации</li>
            </ul>
          </nav>
        </aside> */}
        <main className="admin-main" style={{ overflowY: "scroll" }}>
          <div style={{ display: "flex", flexDirection: "row" }}>
            <h1 className="admin-title">Дашборд</h1>
          </div>
          <Tabs
            value={activeTab}
            style={{ color: "white", marginTop: 20, width: "100%" }}
            onChange={(e, newValue) => {
              setActiveTab(newValue);
              if (newValue === "TOTAL_ACTIVE_SUBSCRIPTIONS") {
                updateTariffesData();
              }
            }}
            aria-label="basic tabs example"
            variant="scrollable"
          >
            <Tab
              style={{
                color: "white",
                fontFamily: "NT Somic",
                fontWeight: 400,
              }}
              label="Пользователи"
              value={"TOTAL_USERS"}
            />
            <Tab
              style={{
                color: "white",
                fontFamily: "NT Somic",
                fontWeight: 400,
              }}
              label="Загруженные видео"
              value={"TOTAL_UPLOADED_VIDEOS"}
            />
            <Tab
              style={{
                color: "white",
                fontFamily: "NT Somic",
                fontWeight: 400,
              }}
              label="Хранилище"
              value={"TOTAL_STORAGE_USED_MB"}
            />
            <Tab
              style={{
                color: "white",
                fontFamily: "NT Somic",
                fontWeight: 400,
              }}
              label="Длительность видео"
              value={"TOTAL_VIDEO_DURATION_SEC"}
            />
            <Tab
              style={{
                color: "white",
                fontFamily: "NT Somic",
                fontWeight: 400,
              }}
              label="Комментарии"
              value={"TOTAL_COMMENTS"}
            />
            <Tab
              style={{
                color: "white",
                fontFamily: "NT Somic",
                fontWeight: 400,
              }}
              label="Подписки"
              value={"TOTAL_ACTIVE_SUBSCRIPTIONS"}
            />
          </Tabs>
          <div class="select-wrapper">
            <select
              className="date-filter-select"
              value={dateFilter}
              onChange={(e) => onChangeDateFilter(e.target.value)}
            >
              {DATE_FILTERS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <span className="admin-stats-text">
            {`${metricLabel[activeTab]}`} (по состоянию на {yesterdayDate}):{" "}
            <b>{getTotalMetricCount(metricsData[activeTab])}</b>
          </span>
          {activeTab === "TOTAL_ACTIVE_SUBSCRIPTIONS" && (
            <span className="admin-stats-text">{tariffesString}</span>
          )}
          <LineChart
            style={{
              width: isMobile ? "115%" : "90%",
              maxWidth: isMobile ? "115%" : "90%",
              height: isMobile ? 200 : 300,
              minHeight: isMobile ? 200 : 300,
              marginTop: 20,
              marginLeft: isMobile ? "-15%" : 0,
            }}
            responsive
            data={metricsData[activeTab]}
          >
            <CartesianGrid stroke="#FFFFFF10" strokeDasharray="5 5" />
            <Line dataKey={"value"} />
            <XAxis
              padding={{ top: 10 }}
              style={{ fontSize: 12, marginTop: 10 }}
              dataKey="date"
            />
            <YAxis
              dataKey="value"
              allowDecimals={false}
              // domain={([dataMin, dataMax]) => {
              //   let min = Math.max(0, dataMin);
              //   return [min, dataMax];
              // }}
            />
            <Tooltip />
          </LineChart>
          <span className="admin-stats-text">
            {`${metricLabelDiff[activeTab]}`} (по состоянию на {yesterdayDate}):{" "}
            <b>{getTotalMetricCount(metricsData[`${activeTab}_diff`])}</b>
          </span>
          <LineChart
            style={{
              width: isMobile ? "115%" : "90%",
              maxWidth: isMobile ? "115%" : "90%",
              height: isMobile ? 200 : 300,
              minHeight: isMobile ? 200 : 300,
              marginTop: 20,
              marginLeft: isMobile ? "-15%" : 0,
            }}
            responsive
            data={metricsData[`${activeTab}_diff`]}
          >
            <CartesianGrid stroke="#FFFFFF10" strokeDasharray="5 5" />
            <Line dataKey={"value"} />
            <XAxis
              padding={{ top: 10 }}
              style={{ fontSize: 12, marginTop: 10 }}
              dataKey="date"
            />
            <YAxis
              dataKey="value"
              allowDecimals={false}
              // domain={([dataMin, dataMax]) => {
              //   let min = Math.max(0, dataMin);
              //   return [min, dataMax];
              // }}
              // domain={([dataMin, dataMax]) => {
              //   if (activeTab === "TOTAL_USERS") return [0, 70];
              //   const min = dataMin - 0.01 * dataMin;
              //   const max = dataMax + 0.01 * dataMax;
              //   return [min, max];
              // }}
            />
            <Tooltip
              formatter={(value) => {
                return value > 0 ? `+${value}` : `${value}`;
              }}
            />
          </LineChart>
        </main>
      </div>
    </div>
  );
};

export default AdminStatsScreen;
